import * as Sentry from '@sentry/react';

type LogContext = Record<string, unknown>;

const isDev = import.meta.env.DEV;

function normalizeError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  if (error) {
    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error(fallbackMessage);
    }
  }
  return new Error(fallbackMessage);
}

function safeCapture(callback: () => void) {
  try {
    callback();
  } catch {
    // noop – avoid throwing during logging
  }
}

export function reportError(message: string, error?: unknown, context?: LogContext) {
  const err = normalizeError(error, message);

  if (isDev) {
    console.error(message, err, context ?? '');
  }

  safeCapture(() => {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('context', context);
      }
      scope.setLevel('error');
      scope.setExtra('message', message);
      Sentry.captureException(err);
    });
  });
}

export function reportWarning(message: string, context?: LogContext) {
  if (isDev) {
    console.warn(message, context ?? '');
  }

  safeCapture(() => {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('context', context);
      }
      scope.setLevel('warning');
      Sentry.captureMessage(message, 'warning');
    });
  });
}

export function reportInfo(message: string, context?: LogContext) {
  if (isDev) {
    console.info(message, context ?? '');
  }
}

export function reportDebug(message: string, context?: LogContext) {
  if (isDev) {
    console.debug(message, context ?? '');
  }
}
